/**
 * Museum Announcement Slider Component
 * Custom Element: <museum-announcement-slider>
 */
if (!customElements.get('museum-announcement-slider')) {
  class MuseumAnnouncementSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.isPaused = false;

      // Bound event handlers
      this.handlePrev = this.prevSlide.bind(this);
      this.handleNext = this.nextSlide.bind(this);
      this.handleDotClick = this.onDotClick.bind(this);
      this.handleMouseEnter = this.onMouseEnter.bind(this);
      this.handleMouseLeave = this.onMouseLeave.bind(this);
      this.handleFocusIn = this.onFocusIn.bind(this);
      this.handleFocusOut = this.onFocusOut.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
      this.handleBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.initElements();
      if (!this.slides || this.slides.length === 0) return;

      this.readSettings();
      this.bindEvents();
      this.goToSlide(0, false);

      if (this.autoplayEnabled && !this.prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.unbindEvents();
    }

    initElements() {
      this.slides = Array.from(this.querySelectorAll('.museum-announcement__slide'));
      this.dots = Array.from(this.querySelectorAll('.museum-announcement__dot'));
      this.prevBtn = this.querySelector('.museum-announcement__arrow--prev');
      this.nextBtn = this.querySelector('.museum-announcement__arrow--next');
    }

    readSettings() {
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.speed, 10) || 6000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.infiniteLoop = this.dataset.loop === 'true';
      this.slideCount = this.slides.length;
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.handlePrev);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.handleNext);
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.handleDotClick);
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }

      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // Shopify Theme Editor events
      this.closest('section')?.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.closest('section')?.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    unbindEvents() {
      if (this.prevBtn) {
        this.prevBtn.removeEventListener('click', this.handlePrev);
      }
      if (this.nextBtn) {
        this.nextBtn.removeEventListener('click', this.handleNext);
      }

      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this.handleDotClick);
      });

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
      }

      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      this.closest('section')?.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.closest('section')?.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    goToSlide(index, resetAutoplay = true) {
      if (!this.slides || this.slides.length === 0) return;

      let targetIndex = index;
      if (targetIndex >= this.slideCount) {
        targetIndex = this.infiniteLoop ? 0 : this.slideCount - 1;
      } else if (targetIndex < 0) {
        targetIndex = this.infiniteLoop ? this.slideCount - 1 : 0;
      }

      this.currentIndex = targetIndex;

      // Update slides
      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        // Manage keyboard focusability of links inside inactive slides
        const links = slide.querySelectorAll('a, button');
        links.forEach((link) => {
          link.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });

      // Update dots
      this.dots.forEach((dot, idx) => {
        const isActive = idx === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (resetAutoplay && this.autoplayEnabled && !this.isPaused) {
        this.restartAutoplay();
      }
    }

    nextSlide() {
      this.goToSlide(this.currentIndex + 1);
    }

    prevSlide() {
      this.goToSlide(this.currentIndex - 1);
    }

    onDotClick(event) {
      const btn = event.currentTarget;
      const index = parseInt(btn.dataset.index, 10);
      if (!isNaN(index)) {
        this.goToSlide(index);
      }
    }

    onMouseEnter() {
      if (this.pauseOnHover) {
        this.isPaused = true;
        this.stopAutoplay();
      }
    }

    onMouseLeave() {
      if (this.pauseOnHover) {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.prefersReducedMotion()) {
          this.startAutoplay();
        }
      }
    }

    onFocusIn() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      // Check if new focus is still within this slider
      if (!this.contains(event.relatedTarget)) {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.prefersReducedMotion()) {
          this.startAutoplay();
        }
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplayEnabled && !this.isPaused && !this.prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    onBlockSelect(event) {
      const blockId = event.detail.blockId;
      const targetSlide = this.querySelector(`[shopify-attributes][data-index], [data-index]`);
      const targetSlideElement = this.querySelector(`[data-index]`);

      const slideIndex = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target || slide.id.includes(blockId));

      if (slideIndex !== -1) {
        this.isPaused = true;
        this.stopAutoplay();
        this.goToSlide(slideIndex, false);
      }
    }

    onBlockDeselect() {
      this.isPaused = false;
      if (this.autoplayEnabled && !this.prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (this.slideCount <= 1 || this.autoplayTimer) return;
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('museum-announcement-slider', MuseumAnnouncementSlider);
}
